




























import {ref, reactive, computed, defineComponent, PropType} from '@vue/composition-api'
import {Quaternion, Euler, Vector3} from 'three'
import {AnyParam, isAnimatedParam} from '@/params'
import DraggableNumberInput from './DraggableNumberInput.vue'
import ParamAutokeyIcon from './ParamAutokeyIcon.vue'
import ParamKeyframeIcon from './ParamKeyframeIcon.vue'

const xAxis = new Vector3(1, 0, 0)
const yAxis = new Vector3(0, 1, 0)
const zAxis = new Vector3(0, 0, 1)

export default defineComponent({
  name: 'RotationParam',
  components: {
    DraggableNumberInput, ParamAutokeyIcon, ParamKeyframeIcon
  },
  props: {
    param: {
      type: Object as PropType<AnyParam>,
      required: true
    }
  },
  setup(props, context) {
    const qValue = new Quaternion()
    const eulerValue = new Euler()
    const qTemp = new Quaternion()

    function degToRad(deg: number) {
      return deg * Math.PI / 180.0
    }

    function radToDeg(rad: number) {
      return rad * 180.0 / Math.PI
    }

    /** current value as Quaternion */
    const quaternion = computed(() => {
      const val = props.param.value as number[]
      qValue.set(val[0], val[1], val[2], val[3])
      return qValue
    })

    /** current value as Euler angles */
    const euler = computed(() => {
      eulerValue.setFromQuaternion(quaternion.value)
      return eulerValue
    })

    /** Visible values: Euler angles, in degrees */
    const eulerX = computed(() => radToDeg(euler.value.x))
    const eulerY = computed(() => radToDeg(euler.value.y))
    const eulerZ = computed(() => radToDeg(euler.value.z))

    /** Emit input event with new value (in radians) */
    function setParam(val: number, index: number) {
      val = degToRad(val)
      const e = euler.value
      let increment = 0
      switch (index) {
      case 0:
        increment = val - e.x
        qTemp.setFromAxisAngle(xAxis, increment)
        break
      case 1:
        increment = val - e.y
        qTemp.setFromAxisAngle(yAxis, increment)
        break
      case 2:
        increment = val - e.z
        qTemp.setFromAxisAngle(zAxis, increment)
        break
      }
      qValue.premultiply(qTemp).normalize() // rotate by qTemp: qTemp * qValue * v
      context.emit('input', qValue.toArray()) // x,y,z,w
    }

    function toggleAutokey() {
      context.emit('toggleAutokey')
      context.emit('startDrag')     // not really a drag, but this selects the param.
    }

    function toggleKeyframe() {
      context.emit('toggleKeyframe')
      context.emit('startDrag')     // not really a drag, but this selects the param.
    }

    return {toggleAutokey, toggleKeyframe, euler, quaternion,
            eulerX, eulerY, eulerZ, setParam}
  }
})
